"use client";

import { IconRosetteDiscount } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import CopyToClipboard from "react-copy-to-clipboard";
import Copy from "@/assets/icons/copy.svg";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import type { DiscountType } from "@/features/cart/types/discount.type";
import { getLocalePrice } from "@/lib/utilities";
import { cn } from "@/lib/utils";

const BURN_DISCOUNT_CODE_NAME = "294ae0a43b27";

const getDiscountCode = (
  discount: DiscountType,
  t: ReturnType<typeof useTranslations>,
) => {
  switch (discount.type) {
    case "diet_length":
      return t("discountCodeDietLength", {
        amount: discount.amount.gross,
        unit: discount.unit || "currency",
      });
    case "manual": {
      // Manual coupons show the discount value, not the code name — percentage
      // codes as a percentage, everything else as a formatted currency amount.
      const value =
        discount.unit === "percentage"
          ? t("discountCodeOnly", {
              amount: discount.percentage || 0,
              unit: "percentage",
            })
          : `-${getLocalePrice({ price: discount.amount.gross })}`;

      return t("discountCodeAlter", { value });
    }
    default:
      return t("discountCodeWithoutCode", {
        amount:
          discount.unit === "percentage"
            ? discount.percentage || 0
            : discount.amount.gross,
        unit: discount.unit || "currency",
      });
  }
};

export const DiscountCodes = ({
  discountCodes,
  codeCopy,
  className,
  breakSpaces = false,
}: {
  discountCodes?: DiscountType[];
  codeCopy?: boolean;
  className?: string;
  breakSpaces?: boolean;
}) => {
  const t = useTranslations("diets");
  if (!discountCodes) return null;
  if (discountCodes.length === 0) return null;

  const Code = (discount: DiscountType) =>
    codeCopy ? (
      <span>
        <span className="whitespace-break-spaces">
          {getDiscountCode(discount, t)}
        </span>
        <CopyToClipboard
          onCopy={() =>
            toast.success(
              <div className="flex items-center gap-0.5">
                <IconRosetteDiscount
                  className="mr-1 inline-block size-4 min-h-4 min-w-4"
                  stroke={1.5}
                />
                <span>
                  <span>{t("discountCodeCopied")}</span>
                  <span className="font-bold">{discount.name}</span>
                </span>
              </div>,
            )
          }
          text={discount.name}
        >
          <span className="ml-0.5 cursor-pointer font-bold">
            {discount.name}
            <Copy className="ml-0.5 inline-block h-4 min-h-4 w-4 min-w-4" />
          </span>
        </CopyToClipboard>
      </span>
    ) : (
      <span
        className={cn({
          "whitespace-break-spaces": breakSpaces,
          "overflow-clip whitespace-nowrap": !breakSpaces,
        })}
      >
        {getDiscountCode(discount, t)}
      </span>
    );

  // filter out 0% percentage discounts and the specific burn code
  const visibleDiscounts = discountCodes.filter(
    (discount) =>
      discount.name !== BURN_DISCOUNT_CODE_NAME &&
      (discount.unit !== "percentage" || (discount.percentage || 0) > 0),
  );

  return (
    visibleDiscounts.map((discountCode) => (
      <Badge
        className={cn(
          "rounded-full border-0 bg-brand-red/10 px-3 py-1.5 text-brand-red md:px-2",
          className,
          discountCode.type === "manual" && "bg-blue-50 text-brand-blue-400",
        )}
        key={discountCode.name}
        variant="outline"
      >
        <p className="flex items-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap font-normal text-xs leading-4.5 md:font-medium">
          {/* Icon inherits the badge text color, red by default and blue for manual codes. */}
          <IconRosetteDiscount
            className="size-4 min-h-4 min-w-4"
            stroke={1.5}
          />
          <Code {...discountCode} />
        </p>
      </Badge>
    )) || null
  );
};
